/**
 * RegisterForm Component
 * 
 * User registration form with email verification flow.
 * Includes password strength validation and confirmation.
 * 
 * Requirements: 2.1, 2.5
 */

import React, { useState } from 'react';
import { validateEmail, validatePasswordStrength } from '../utils/validation.utils';
import { ErrorAlert } from './ErrorAlert';
import { LoadingSpinner } from './LoadingSpinner';
import { PasswordStrength } from './PasswordStrength';

export interface RegisterFormProps {
  onSubmit: (email: string, password: string, firstName: string, lastName: string, phoneNumber?: string) => Promise<void>;
  onLogin?: () => void;
}

export const RegisterForm: React.FC<RegisterFormProps> = ({ onSubmit, onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: string[] = [];

    if (!email.trim()) {
      newErrors.push('Email is required');
    } else if (!validateEmail(email)) {
      newErrors.push('Please enter a valid email address');
    }

    if (!firstName.trim()) {
      newErrors.push('First name is required');
    }

    if (!lastName.trim()) {
      newErrors.push('Last name is required');
    }

    const passwordValidation = validatePasswordStrength(password);
    if (!passwordValidation.isValid) {
      newErrors.push(...passwordValidation.feedback);
    }

    if (password !== confirmPassword) {
      newErrors.push('Passwords do not match');
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors([]);

    try {
      await onSubmit(email, password, firstName, lastName, phoneNumber || undefined);
    } catch (error) {
      setErrors([error instanceof Error ? error.message : 'Registration failed']);
    } finally {
      setIsLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '6px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#333',
  };

  const buttonStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px',
    backgroundColor: '#3498db',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: 600,
    cursor: isLoading ? 'not-allowed' : 'pointer',
    opacity: isLoading ? 0.7 : 1,
  };

  return (
    <form onSubmit={handleSubmit} className="register-form">
      {errors.length > 0 && <ErrorAlert message={errors} onDismiss={() => setErrors([])} />}

      <div style={{ marginBottom: '16px' }}>
        <label htmlFor="email" style={labelStyle}>
          Email Address
        </label>
        <input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isLoading}
          autoComplete="email"
          style={inputStyle}
          aria-required="true"
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
        <div>
          <label htmlFor="firstName" style={labelStyle}>
            First Name
          </label>
          <input
            id="firstName"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            disabled={isLoading}
            autoComplete="given-name"
            style={inputStyle}
            aria-required="true"
          />
        </div>
        <div>
          <label htmlFor="lastName" style={labelStyle}>
            Last Name
          </label>
          <input
            id="lastName"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            disabled={isLoading}
            autoComplete="family-name"
            style={inputStyle}
            aria-required="true"
          />
        </div>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label htmlFor="phoneNumber" style={labelStyle}>
          Phone Number (Optional)
        </label>
        <input
          id="phoneNumber"
          type="tel"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          disabled={isLoading}
          autoComplete="tel"
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label htmlFor="password" style={labelStyle}>
          Password
        </label>
        <input
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={isLoading}
          autoComplete="new-password"
          style={inputStyle}
          aria-required="true"
        />
        <PasswordStrength password={password} />
      </div>

      <div style={{ marginBottom: '24px' }}>
        <label htmlFor="confirmPassword" style={labelStyle}>
          Confirm Password
        </label>
        <input
          id="confirmPassword"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={isLoading}
          autoComplete="new-password"
          style={inputStyle}
          aria-required="true"
        />
      </div>

      <button type="submit" disabled={isLoading} style={buttonStyle}>
        {isLoading ? <LoadingSpinner size="small" /> : 'Create Account'}
      </button>

      {onLogin && (
        <div style={{ marginTop: '24px', textAlign: 'center', fontSize: '14px', color: '#666' }}>
          Already have an account?{' '}
          <button
            type="button"
            onClick={onLogin}
            disabled={isLoading}
            style={{
              background: 'none',
              border: 'none',
              color: '#3498db',
              cursor: 'pointer',
              textDecoration: 'underline',
              fontSize: '14px',
            }}
          >
            Sign in
          </button>
        </div>
      )}
    </form>
  );
};
